import AuthService from '@/services/AuthService';
import { notify } from '@/utils/notifications';
import jwtDecode from 'jwt-decode';
import axios from 'axios';

/**
 * Helper function to check if a token is expired
 * 
 * @param {string} token - JWT token to check
 * @returns {boolean} True if token is expired or invalid
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
};

/**
 * Helper function to set auth header for all requests
 * 
 * @param {string} token - JWT token to set in header
 */
const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

/**
 * Initial state for auth module
 */
const getInitialAuthState = () => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const user = localStorage.getItem('user');
  
  // Consider user logged in if we have both tokens (even if access token is expired)
  // The router guard will handle token refresh if needed
  const hasTokens = !!(accessToken && refreshToken);
  
  return {
    status: {
      loggedIn: hasTokens,
      loading: false,
      error: null
    },
    tokens: {
      accessToken: accessToken || null,
      refreshToken: refreshToken || null
    },
    user: user ? JSON.parse(user) : null
  };
};

const initialState = getInitialAuthState();

// Initialize auth header from localStorage if token exists
if (initialState.tokens.accessToken) {
  setAuthHeader(initialState.tokens.accessToken);
}

/**
 * Auth module for Vuex store
 */
export const auth = {
  namespaced: true,
  state: initialState,
  
  actions: {
    // Updates needed for auth.js module to integrate with workspace

// 1. Update the loginSuccess action to initialize workspace
    async loginSuccess({ commit, dispatch }, userData) {
      commit('loginSuccess', userData);

      // Fetch user profile after login - handle both response structures
      const userId = userData.user ? userData.user.id : userData.userId;

      // For OAuth login, userData might only have tokens, so always fetch profile if logged in
      if (userId || (userData.accessToken && userData.refreshToken)) {
        console.log('🔄 [auth/loginSuccess] Dispatching user profile fetch...');
        console.trace('📍 [auth/loginSuccess] Call stack for profile dispatch');
        dispatch('user/fetchProfile', null, { root: true });
      }

      // NOTE: Workspace initialization is now handled lazily by components that need it
      // This prevents duplicate initialization and race conditions

      return Promise.resolve(userData);
    },

// 2. Update the login action to use loginSuccess action (prevents duplicates)
    async login({ commit, dispatch }, loginRequest) {
      commit('loginRequest');

      try {
        const response = await AuthService.login(loginRequest);

        // Use the loginSuccess action which handles profile fetching and notifications
        // This prevents duplicate profile calls
        await dispatch('loginSuccess', response.data);

        // For email login, also initialize workspace (OAuth uses lazy initialization)
        // Pass forceDefaultWorkspace flag to prioritize default workspace on fresh login
        dispatch('workspace/initialize', { forceDefaultWorkspace: true }, { root: true });

        return Promise.resolve(response.data);
      } catch (error) {
        commit('loginFailure', error);

        // Show error notification
        let errorMessage = 'Login failed';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        notify('error', errorMessage);

        return Promise.reject(error);
      }
    },

// 3. Update the logout action to reset workspace state
    async logout({ commit, dispatch, state }, allSessions = false) {
      try {
        if (state.status.loggedIn) {
          await AuthService.logout(allSessions);
        }
      } catch (error) {
        console.error('Logout error:', error);
      }

      // Reset auth state
      commit('logout');

      // Reset user state
      dispatch('user/resetUserState', null, { root: true });

      // Reset workspace state
      dispatch('workspace/resetWorkspaceState', null, { root: true });

      return Promise.resolve();
    },
    
    /**
     * Refresh the access token using the refresh token
     * 
     * @param {Object} context - Vuex context
     * @param {Function} context.commit - Commit function
     * @param {Function} context.state - State object
     * @returns {Promise} Promise with the refresh result
     */
    async refreshToken({ commit, state }) {
      // Don't try to refresh if no refresh token
      if (!state.tokens.refreshToken) {
        commit('logout');
        return Promise.reject(new Error('No refresh token available'));
      }
      
      commit('refreshTokenRequest');
      
      try {
        const response = await AuthService.refreshToken({
          refreshToken: state.tokens.refreshToken,
          rememberMe: true // Default to remember me for token refresh
        });
        
        commit('refreshTokenSuccess', response.data);
        return Promise.resolve(response.data);
      } catch (error) {
        commit('refreshTokenFailure');
        commit('logout');
        return Promise.reject(error);
      }
    },
    
    /**
     * Check authentication status and refresh token if needed
     * 
     * @param {Object} context - Vuex context
     * @param {Function} context.dispatch - Dispatch function
     * @param {Function} context.state - State object
     * @returns {Promise<boolean>} Promise resolving to authentication status
     */
    async checkAuth({ dispatch, state }) {
      // Not logged in
      if (!state.status.loggedIn) {
        return Promise.resolve(false);
      }
      
      // Check if access token is expired
      if (isTokenExpired(state.tokens.accessToken)) {
        // Try to refresh token
        try {
          await dispatch('refreshToken');
          return Promise.resolve(true);
        } catch (error) {
          return Promise.resolve(false);
        }
      }
      
      return Promise.resolve(true);
    },
    
    /**
     * Set up auth interceptors for automatic token refresh
     * 
     * @param {Object} context - Vuex context
     * @param {Function} context.dispatch - Dispatch function
     */
    setupAuthInterceptors({ dispatch }) {
      // Set up interceptors with callbacks
      AuthService.setupAxiosInterceptors(
        // Refresh token callback
        async () => {
          return await dispatch('refreshToken');
        },
        // Logout callback
        async () => {
          return await dispatch('logout');
        }
      );
    }
  },
  
  mutations: {
    /**
     * Set state for login request
     * 
     * @param {Object} state - Current state
     */
    loginRequest(state) {
      state.status = {
        loggedIn: false,
        loading: true,
        error: null
      };
    },
    
    /**
     * Update state after successful login
     * 
     * @param {Object} state - Current state
     * @param {Object} data - Login response data
     */
    loginSuccess(state, data) {
      // Update state
      state.status = {
        loggedIn: true,
        loading: false,
        error: null
      };
      state.tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken
      };

      // Handle both LoginResponse (flat structure) and AuthResponse (nested structure)
      if (data.user) {
        // AuthResponse structure (social login)
        state.user = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName
        };
      } else {
        // LoginResponse structure (regular login)
        state.user = {
          id: data.userId,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName
        };
      }
      
      // Store in localStorage
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(state.user));
      
      // Set auth header
      setAuthHeader(data.accessToken);
    },
    
    /**
     * Handle login failure
     * 
     * @param {Object} state - Current state
     * @param {Error} error - Error object
     */
    loginFailure(state, error) {
      state.status = {
        loggedIn: false,
        loading: false,
        error: error
      };
      state.tokens = {
        accessToken: null,
        refreshToken: null
      };
      state.user = null;
      
      // Clear localStorage and auth header
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setAuthHeader(null);
    },
    
    /**
     * Reset state on logout
     * 
     * @param {Object} state - Current state
     */
    logout(state) {
      state.status = {
        loggedIn: false,
        loading: false,
        error: null
      };
      state.tokens = {
        accessToken: null,
        refreshToken: null
      };
      state.user = null;
      
      // Clear localStorage and auth header
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setAuthHeader(null);
    },
    
    /**
     * Set state for token refresh request
     * 
     * @param {Object} state - Current state
     */
    refreshTokenRequest(state) {
      state.status.loading = true;
    },
    
    /**
     * Update state after successful token refresh
     * 
     * @param {Object} state - Current state
     * @param {Object} data - Token refresh response data
     */
    refreshTokenSuccess(state, data) {
      state.status = {
        loggedIn: true,
        loading: false,
        error: null
      };
      state.tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken
      };
      
      // Update localStorage
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      
      // Set auth header
      setAuthHeader(data.accessToken);
    },
    
    /**
     * Handle token refresh failure
     * 
     * @param {Object} state - Current state
     */
    refreshTokenFailure(state) {
      state.status = {
        loggedIn: false,
        loading: false,
        error: new Error('Failed to refresh token')
      };
    }
  },
  
  getters: {
    /**
     * Check if user is logged in
     * 
     * @param {Object} state - Current state
     * @returns {boolean} True if logged in
     */
    isLoggedIn(state) {
      return state.status.loggedIn;
    },
    
    /**
     * Check if auth operations are loading
     * 
     * @param {Object} state - Current state
     * @returns {boolean} True if loading
     */
    isLoading(state) {
      return state.status.loading;
    },
    
    /**
     * Get current user data
     * 
     * @param {Object} state - Current state
     * @returns {Object} User data
     */
    getUser(state) {
      return state.user;
    },
    
    /**
     * Get user's full name
     * 
     * @param {Object} state - Current state
     * @returns {string} Full name or empty string
     */
    getFullName(state) {
      if (!state.user) return '';
      return `${state.user.firstName} ${state.user.lastName}`;
    },
    
    /**
     * Get access token
     * 
     * @param {Object} state - Current state
     * @returns {string} Access token
     */
    getAccessToken(state) {
      return state.tokens.accessToken;
    },
    
    /**
     * Get refresh token
     * 
     * @param {Object} state - Current state
     * @returns {string} Refresh token
     */
    getRefreshToken(state) {
      return state.tokens.refreshToken;
    },
    
    /**
     * Get current error
     * 
     * @param {Object} state - Current state
     * @returns {Error} Error object
     */
    getError(state) {
      return state.status.error;
    },
    
    /**
     * Get user ID
     * 
     * @param {Object} state - Current state
     * @returns {string} User ID
     */
    getUserId(state) {
      return state.user ? state.user.id : null;
    },
    
    /**
     * Get user email
     * 
     * @param {Object} state - Current state
     * @returns {string} User email
     */
    getUserEmail(state) {
      return state.user ? state.user.email : null;
    }
  }
};

export default auth;